import React, { useEffect, useState } from "react";
import { Loader2, Send, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { profile } from "../../data/mock";
import { COACHING_PROJECT_TYPE } from "../../data/coaching";
import SectionHeading from "./SectionHeading";
import { CoachingFields, emptyCoachingValues, fieldCls } from "./CoachingFields";
import { getValidatedCampaign, trackConfirmedCoachingSubmission } from "../../lib/analytics";

const labelCls = "block text-[#C8CED8] text-xs tracking-widest uppercase mb-2";

const CoachingForm = ({ t, locale, selectedPath, onPathChange }) => {
  const f = t.form;
  const blank = () => ({ name: "", email: "", message: "", ...emptyCoachingValues(locale) });
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setForm((s) => ({ ...s, coaching_path: selectedPath }));
  }, [selectedPath]);

  const onChange = (k) => (v) => {
    setForm((s) => ({ ...s, [k]: v }));
    if (k === "coaching_path") onPathChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast(f.errorTitle, { description: f.requiredNote });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        ...form,
        location: form.session_format === "in-person" ? form.location : "",
        project_type: COACHING_PROJECT_TYPE,
        source: `coaching-${locale}`,
        ...getValidatedCampaign(),
      });
      trackConfirmedCoachingSubmission();
      setSent(true);
      toast(f.successTitle, { description: f.successBody });
      setForm(blank());
      onPathChange("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast(f.errorTitle, { description: typeof detail === "string" ? detail : f.errorBody });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="request" data-testid="coaching-request" className="relative py-16 md:py-24 bg-[#070A12] scroll-mt-16">
      <div className="max-w-6xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <SectionHeading index="05" title={f.title} subtitle={f.intro} />
          <a href={`mailto:${profile.email}`} data-testid="coaching-direct-email"
            className="mt-8 inline-flex items-center gap-3 text-sm text-[#C8CED8] hover:text-[#1E90FF] transition-colors break-all">
            <Mail size={16} className="text-[#1E90FF] shrink-0" />
            <span><span className="block text-[10px] tracking-[0.2em] text-[#A1A7B3] uppercase">{t.ui.contactDirect}</span>{profile.email}</span>
          </a>
        </div>

        <form onSubmit={submit} noValidate data-testid="coaching-form" className="ph-no-capture lg:col-span-8 bb-card rounded-2xl p-5 md:p-8 space-y-5">
          {sent && (
            <div data-testid="coaching-form-success" className="flex gap-3 p-4 rounded-md border border-[#1E90FF]/40 bg-[#1E90FF]/10 text-sm text-[#F3F6FA]">
              <CheckCircle2 size={18} className="text-[#1E90FF] shrink-0" />
              <span><strong>{f.successTitle}.</strong> {f.successBody}</span>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="coaching-name" className={labelCls}>{f.nameLabel} *</label>
              <input id="coaching-name" data-testid="coaching-name" autoComplete="name" maxLength={120}
                value={form.name} onChange={(e) => onChange("name")(e.target.value)} className={fieldCls} />
            </div>
            <div>
              <label htmlFor="coaching-email" className={labelCls}>{f.emailLabel} *</label>
              <input id="coaching-email" data-testid="coaching-email" type="email" inputMode="email" autoComplete="email"
                value={form.email} onChange={(e) => onChange("email")(e.target.value)} className={fieldCls} />
            </div>
          </div>
          <CoachingFields t={f} values={form} onChange={onChange} idPrefix="coaching" showPath />
          <div>
            <label htmlFor="coaching-objective" className={labelCls}>{f.objectiveLabel} *</label>
            <textarea id="coaching-objective" data-testid="coaching-objective" rows={5} maxLength={4000}
              value={form.message} onChange={(e) => onChange("message")(e.target.value)} placeholder={f.objectivePlaceholder}
              className={`${fieldCls} h-auto py-3 resize-none`} />
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 pt-1">
            <p className="text-xs text-[#A1A7B3]">{f.requiredNote}</p>
            <button type="submit" disabled={submitting} data-testid="coaching-submit-btn"
              className="h-12 px-6 inline-flex items-center justify-center rounded-full bg-[#1E90FF] hover:bg-[#0066CC] disabled:opacity-60 text-white font-semibold transition-colors">
              {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
              {submitting ? f.submitting : f.submit}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default CoachingForm;
